import { apiFetch } from "@/lib/api-client";
import type { WizardDraft } from "./wizard/serializers";

/** 后端解析结果：与向导表单模型同构 */
export type ParsedDraft = WizardDraft;

export interface Product {
  id: number;
  company_id: number;
  key: string;
  name: string;
  calc_strategy: string;
  schema_json: string;
  sort_order: number;
  is_active: number;
  /** 列表接口附带的统计（product_list 的 COUNT） */
  rule_count?: number;
  modifier_count?: number;
}

export interface Rule {
  id?: number;
  product_id?: number;
  parent_id?: number | null;
  dimension?: string;
  condition_json?: string | Record<string, unknown>;
  label?: string;
  price?: number;
  unit?: string;
  sort_order?: number;
  [k: string]: unknown;
}

export interface Modifier {
  id?: number;
  product_id?: number;
  modifier_type: string;
  params_json?: string | Record<string, unknown>;
  is_active?: number;
  [k: string]: unknown;
}

export interface CreateProductPayload {
  key: string;
  name: string;
  calc_strategy: string;
  schema_json?: string;
  sort_order?: number;
}

export interface UpdateProductPayload {
  name?: string;
  calc_strategy?: string;
  schema_json?: string;
  sort_order?: number;
  is_active?: number;
}

function withScope(path: string, scope?: string) {
  return scope ? `${path}${path.includes("?") ? "&" : "?"}scope=${encodeURIComponent(scope)}` : path;
}

export async function listProducts(includeInactive = true, scope?: string) {
  const path = withScope(`/api/admin/products?include_inactive=${includeInactive}`, scope);
  return apiFetch<{ success: boolean; data: Product[] }>(path);
}

export async function createProduct(p: CreateProductPayload, scope?: string) {
  return apiFetch<{ success: boolean; id: number }>(withScope("/api/admin/products", scope), {
    method: "POST",
    body: JSON.stringify(p),
  });
}

export async function updateProduct(id: number, p: UpdateProductPayload, scope?: string) {
  return apiFetch<{ success: boolean }>(withScope(`/api/admin/products/${id}`, scope), {
    method: "PATCH",
    body: JSON.stringify(p),
  });
}

export async function deleteProduct(id: number, scope?: string) {
  return apiFetch<{ success: boolean }>(withScope(`/api/admin/products/${id}`, scope), {
    method: "DELETE",
  });
}

export async function listRules(productId: number, scope?: string) {
  return apiFetch<{ success: boolean; data: Rule[] }>(
    withScope(`/api/admin/products/${productId}/rules`, scope),
  );
}

export async function replaceRules(productId: number, rules: Rule[], scope?: string) {
  return apiFetch<{ success: boolean }>(
    withScope(`/api/admin/products/${productId}/rules`, scope),
    { method: "PUT", body: JSON.stringify({ rules }) },
  );
}

export async function listModifiers(productId: number, scope?: string) {
  return apiFetch<{ success: boolean; data: Modifier[] }>(
    withScope(`/api/admin/products/${productId}/modifiers`, scope),
  );
}

export async function replaceModifiers(productId: number, modifiers: Modifier[], scope?: string) {
  return apiFetch<{ success: boolean }>(
    withScope(`/api/admin/products/${productId}/modifiers`, scope),
    { method: "PUT", body: JSON.stringify({ modifiers }) },
  );
}

/** AI 解析产品描述 → 向导草稿。只解析不落库。 */
export async function parseProductDescription(description: string) {
  return apiFetch<{ success: boolean; draft: ParsedDraft; notes: string[] }>(
    "/api/admin/products/parse",
    { method: "POST", body: JSON.stringify({ description }) },
  );
}

export interface CloneGlobalResult {
  products: number;
  prompts: number;
  llm_configs: number;
}

/** 从全局默认（company_id=0）克隆产品/提示词/LLM 配置到当前公司。force=true 先删除再克隆 */
export async function cloneGlobal(force: boolean) {
  return apiFetch<{ success: boolean; cloned: CloneGlobalResult }>(
    `/api/admin/clone-global?force=${force ? "true" : "false"}`,
    { method: "POST" },
  );
}
